/* eslint-disable react/no-danger */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable jsx-a11y/label-has-associated-control */
import classNames from "classnames";
import Button from "components/Button";
import Label from "components/Label";
import Radio from "components/Radio";
import TextField from "components/TextField";
import { FormattedMessage, useIntl } from "gatsby-plugin-intl";
import envelope from "icons/envelope.svg";
import marker from "icons/marker.svg";
import { OutlinedReactSelect } from "components/SelectField/ReactSelect";
import smartphone from "icons/smartphone.svg";
import user from "icons/user.svg";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useCities, useNearestCity } from "hooks/city";
import { gtagsActions, gtagsCategories, gtagsSend } from "utils/gtagsUtils";
import { sendBecomePartnerInfo } from "../../services/data-manager";
import { FileText, Calendar, UserCheck, ShieldCheck, Share2, X } from "lucide-react";
import "./become-franchisee.scss";

const classes = {
  field:
    "w-full border-gray-200 p-4 rounded-lg ltr:pl-12 rtl:pr-12 bg-gray-100",
};

/* Steps */
const STEP_1 = "step-1";
const STEP_2 = "step-2";
const STEP_3 = "step-3";

/* Send statuses */
const ERROR = "error";

const TermsAndConditionsPopup = ({ isOpen, onClose }) => {
  const { formatMessage } = useIntl();
  const currentDate = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl border-4 border-orange-500 p-4 sm:p-6 md:p-8  max-w-4xl w-full my-4  overflow-y-auto  relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <X size={24} />
        </button>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-orange-100 rounded-full mb-4">
            <FileText size={24} className="text-orange-600" />
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">
            <FormattedMessage id="becomeFranchisee.terms_title" />
          </h1>
          <p className="text-gray-500 text-sm flex items-center justify-center">
            <Calendar size={16} className="mr-2 text-orange-600" />
            <FormattedMessage id="becomeFranchisee.last_updated" />: {currentDate}
          </p>
        </div>
        <div className="mt-6 sm:mt-8 space-y-6 sm:space-y-8">
          <section>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <UserCheck size={20} className="mr-3 text-orange-600" />
            </h2>
            <div className="text-gray-600 text-sm sm:text-base leading-relaxed space-y-4">
              <p
                dangerouslySetInnerHTML={{
                  __html: formatMessage({ id: "becomeFranchisee.terms_paragraphe_1" }),
                }}
              />
            </div>
          </section>
          <section>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Share2 size={20} className="mr-3 text-orange-600" />
            </h2>
            <div className="text-gray-600 text-sm sm:text-base leading-relaxed space-y-4">
              <p
                dangerouslySetInnerHTML={{
                  __html: formatMessage({ id: "becomeFranchisee.terms_paragraphe_2" }),
                }}
              />
            </div>
          </section>
          <section>
            <div className="text-gray-600 text-sm sm:text-base leading-relaxed space-y-4">
              <p
                dangerouslySetInnerHTML={{
                  __html: formatMessage({ id: "becomeFranchisee.terms_paragraphe_3" }),
                }}
              />
            </div>
          </section>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 sm:p-5 text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full mb-3">
              <ShieldCheck size={20} className="text-orange-600" />
            </div>
            <p
              dangerouslySetInnerHTML={{
                __html: formatMessage({ id: "becomeFranchisee.terms_paragraphe_4" }),
              }}
            />
          </div>
        </div>
        <div className="mt-6 text-center">
          <Button
            type="button"
            onClick={onClose}
            variant="default"
            className="w-full sm:w-64 mx-auto"
          >
            <FormattedMessage id="general.close" />
          </Button>
        </div>
      </div>
    </div>
  );
};

TermsAndConditionsPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const BecomeFranchiseeForm = () => {
  const {
    handleSubmit,
    register,
    errors,
    control,
    setValue,
    triggerValidation,
  } = useForm();
  const { formatMessage } = useIntl();

  const [activeStep, setActiveStep] = useState(STEP_1);

  const [sendStatus, setSendStatus] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);

  const handleFormSubmit = values => {
    const data = { ...values, requestType: "DEMANDEFRANCHISE" };
    gtagsSend(gtagsCategories.LEAD, gtagsActions.BECOME_FRANCHISE_FIRST_STEP);
    sendBecomePartnerInfo(data)
      .then(() => setActiveStep(STEP_3))
      .catch(() => setSendStatus(ERROR));
  };

  return (
    <div className="bg-white relative flex justify-center py-20 rounded-lg shadow-md">
      <Progress step={activeStep} />

      <TermsAndConditionsPopup
        isOpen={showTermsPopup}
        onClose={() => setShowTermsPopup(false)}
      />
      <div className="w-10/12 xs:w-4/6">
        <form
          className=" w-full flex flex-no-wrap"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <div
            className={classNames("form-step min-w-full", {
              active: activeStep === STEP_1,
            })}
          >
            <h3 className="text-2xl font-semibold text-center mb-6">
              <FormattedMessage id="becomeFranchisee.informationForm" />
            </h3>

            <StepOne
              register={register}
              errors={errors}
              sendStatus={sendStatus}
              control={control}
              setValue={setValue}
              onNextClick={async () => {
                const valid = await triggerValidation([
                  "civility",
                  "name",
                  "phoneNumber",
                  "email",
                  "city",
                ]);
                if (valid) setActiveStep(STEP_2);
              }}
            />
          </div>
          {/* step 2 fields */}
          <div
            className={classNames("form-step min-w-full ", {
              active: activeStep === STEP_2,
            })}
          >
            <h3 className="text-2xl font-semibold text-center mb-6">
              <FormattedMessage id="becomeFranchisee.informationForm" />
            </h3>

            <StepTwo
              register={register}
              errors={errors}
              sendStatus={sendStatus}
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
              setShowTermsPopup={setShowTermsPopup}
            />
          </div>

          {/* Success  */}
          <div
            className={classNames("form-step min-w-full ", {
              active: activeStep === STEP_3,
            })}
          >
            {activeStep === STEP_3 && (
              <div>
                <img
                  className="w-32 mb-3 mx-auto"
                  src="/images/favicon.png"
                  alt="Smile"
                />
                <h2 className="text-4xl text-center text-primary">
                  <FormattedMessage id="general.thanks" />!
                </h2>

                <p
                  className="text-lg text-center mt-10"
                  dangerouslySetInnerHTML={{
                    __html: formatMessage({
                      id: "becomeFranchisee.success",
                    }),
                  }}
                />
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

/* -------------------- */

const StepOne = ({ register, errors, setValue, control, onNextClick }) => {
  const [fetchCities, { data, error: citiesError, loading }] = useCities();
  const { nearestCity, loading: fetchingNearestCity } = useNearestCity();
  const { formatMessage } = useIntl();

  // const cities = React.useMemo(() => {
  //   return [
  //     { label: "Casablanca", value: "Casablanca" },
  //     { label: "Rabat", value: "Rabat" },
  //     { label: "Marrakech", value: "Marrakech" }
  //   ];
  // }, []);

  const cities = React.useMemo(() => {
    return (
      data?.content?.map(({ name }) => ({
        label: name,
        value: name,
      })) || []
    );
  }, [data]);

  React.useEffect(() => {
    fetchCities({ query: "pageSize=1000" });
  }, []);

  React.useEffect(() => {
    if (nearestCity) {
      setValue("city", nearestCity.name);
    }
  }, [nearestCity]);

  return (
    <div className="py-1">
      <div className="mb-5">
        <Label
          required
          color={null}
          className="block mb-1 text-gray-600"
          labelKey="becomeFranchisee.fields.civility"
        />
        <div className="flex justify-around">
          <div className="flex items-center">
            <Radio
              name="civility"
              id="civility--mr"
              validationError={errors.civility}
              value="mr"
              inputRef={register({
                required: formatMessage({ id: "validation.required" }),
              })}
            />
            <label
              htmlFor="civility--mr"
              className="ltr:ml-2 rtl:mr-2 cursor-pointer"
            >
              <FormattedMessage id="becomeFranchisee.fields.mr" />
            </label>
          </div>

          <div className="flex items-center">
            <Radio
              name="civility"
              id="civility--mme"
              validationError={errors.civility}
              value="mme"
              inputRef={register({
                required: formatMessage({ id: "validation.required" }),
              })}
            />
            <label
              htmlFor="civility--mme"
              className="ltr:ml-2 rtl:mr-2 cursor-pointer"
            >
              <FormattedMessage id="becomeFranchisee.fields.mme" />
            </label>
          </div>
        </div>
        {errors.civility && (
          <span className="mt-1 block text-red-500">
            {errors.civility.message}
          </span>
        )}
      </div>

      <div className="mb-5">
        <Label
          required
          color={null}
          className="block mb-1 text-gray-600"
          labelKey="fields.nameAndLastname"
        />
        <TextField
          name="name"
          variant="outlined"
          className={classes.field}
          validationError={errors.name}
          placeholder={formatMessage({
            id: "fields.nameAndLastname.placeholder",
          })}
          inputRef={register({
            required: formatMessage({ id: "validation.required" }),
          })}
          startIcon={<object data={user} width="18pt" aria-label="name icon" />}
        />
      </div>

      <div className="mb-5">
        <Label
          required
          color={null}
          className="block mb-1 text-gray-600"
          labelKey="fields.phone"
        />
        <TextField
          type="number"
          name="phoneNumber"
          variant="outlined"
          className={classes.field}
          validationError={errors.phoneNumber}
          placeholder={formatMessage({
            id: "fields.phone.placeholder",
          })}
          inputRef={register({
            required: formatMessage({ id: "validation.required" }),
          })}
          startIcon={
            <object data={smartphone} width="18pt" aria-label="name icon" />
          }
        />
      </div>

      <div className="mb-5">
        <Label
          required
          color={null}
          className="block mb-1 text-gray-600"
          labelKey="fields.email"
        />
        <TextField
          name="email"
          variant="outlined"
          className={classes.field}
          validationError={errors.email}
          placeholder={formatMessage({
            id: "fields.email.placeholder",
          })}
          inputRef={register({
            required: formatMessage({ id: "validation.required" }),
          })}
          startIcon={
            <object data={envelope} width="18pt" aria-label="name icon" />
          }
        />
      </div>

      <div className="mb-5">
        <Label
          required
          color={null}
          className="block mb-1 text-gray-600"
          labelKey="fields.city"
        />

        <Controller
          name="city"
          control={control}
          rules={{
            required: formatMessage({ id: "validation.required" }),
          }}
          as={({ onChange, value }) => (
            <OutlinedReactSelect
              placeholder={formatMessage({
                id: "fields.city.placeholder",
              })}
              options={cities}
              loading={loading || fetchingNearestCity}
              value={value}
              onChange={option => onChange(option.value)}
              icon={<object data={marker} width="18pt" aria-label="city" />}
            />
          )}
        />
        {errors.city && (
          <span className="mt-1 block text-red-500">{errors.city.message}</span>
        )}
        {citiesError && (
          <div className="text-sm text-red-600 bg-red-200 px-2 py-2 mt-2 rounded-sm">
            <FormattedMessage id="errors.citiesLoading" />
          </div>
        )}
      </div>

      <Button
        type="button"
        onClick={onNextClick}
        variant="default"
        className="w-full md:w-64 mx-auto"
      >
        <FormattedMessage id="directions.next" />
      </Button>
    </div>
  );
};

StepOne.propTypes = {
  register: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.shape(),
};

/* -------------------- */

const StepTwo = ({ register, errors, sendStatus, termsAccepted, setTermsAccepted, setShowTermsPopup }) => {
  const { formatMessage } = useIntl();

  return (
    <div className="py-1">
      <div className="mb-5">
        <Label
          required
          color={null}
          className="block mb-1 text-gray-600"
          labelKey="becomeFranchisee.fields.shop"
        />
        <div className="flex justify-between">
          <div className="flex items-center">
            <Radio
              name="shop"
              id="shop--yes"
              validationError={errors.shop}
              // eslint-disable-next-line react/jsx-boolean-value
              value={true}
              inputRef={register({
                required: formatMessage({ id: "validation.required" }),
              })}
            />
            <label
              htmlFor="shop--yes"
              className="ltr:ml-2 rtl:mr-2 cursor-pointer"
            >
              <FormattedMessage id="becomeFranchisee.fields.yes" />
            </label>
          </div>

          <div className="flex items-center">
            <Radio
              name="shop"
              id="shop--no"
              validationError={errors.shop}
              value={false}
              inputRef={register({
                required: formatMessage({ id: "validation.required" }),
              })}
            />
            <label
              htmlFor="shop--no"
              className="ltr:ml-2 rtl:mr-2 cursor-pointer"
            >
              <FormattedMessage id="becomeFranchisee.fields.no" />
            </label>
          </div>
        </div>

        {errors.shop && (
          <span className="mt-1 block text-red-500">{errors.shop.message}</span>
        )}
      </div>

      <div className="mb-5">
        <Label
          color={null}
          className="block mb-1 text-gray-600"
          labelKey="becomeFranchisee.fields.neighborhood"
        />
        <TextField
          name="neighborhood"
          variant="outlined"
          className={classes.field}
          placeholder={formatMessage({
            id: "becomeFranchisee.fields.neighborhood.placeholder",
          })}
          inputRef={register}
          startIcon={<object data={marker} width="18pt" aria-label="icon" />}
        />
      </div>

      <div className="mb-5">
        <label className="flex items-start cursor-pointer group">
          <input
            type="checkbox"
            name="terms"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="sr-only"
            ref={register({
              required: formatMessage({ id: "validation.required" }),
            })}
          />
          <div
            className={classNames(
              "w-4 h-4 rounded border-2 mr-2 mt-0.5 flex items-center justify-center transition-all duration-200",
              {
                "border-orange-500 bg-orange-500": termsAccepted,
                "border-red-300": errors.terms,
                "border-gray-300 group-hover:border-orange-300": !termsAccepted && !errors.terms,
              }
            )}
          >
            {termsAccepted && (
              <svg
                className="w-2 h-2 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            )}
          </div>
          <span className="text-xs text-gray-500 leading-relaxed">
            <FormattedMessage id="becomeFranchisee.terms_checkbox_text" defaultMessage="J'accepte les " />
            <Button
              type="button"
              onClick={() => setShowTermsPopup(true)}
              className="text-orange-400 hover:text-orange-600 underline font-medium"
            >
              <FormattedMessage id="becomeFranchisee.terms_link_text" defaultMessage="conditions générales d'utilisation" />
            </Button>{" "}
            <FormattedMessage id="becomeFranchisee.terms_checkbox_text_last" defaultMessage="de Chaabi Cash" />
            <span className="text-red-500 ml-1">*</span>
          </span>
        </label>
        {errors.terms && (
          <span className="mt-1 block text-red-500">{errors.terms.message}</span>
        )}
      </div>
      <Button
        type="submit"
        variant="default"
        className="w-full md:w-64 mx-auto"
        disabled={!termsAccepted}
      >
        <FormattedMessage id="becomeFranchisee.sendRequest" />
      </Button>
      <div
        className={classNames("mt-4 rtl:mr-auto ltr:ml-auto", {
          "text-red-500": sendStatus === ERROR,
        })}
      >
        {sendStatus && formatMessage({ id: `contact.send.${sendStatus}` })}
      </div>
    </div>
  );
};

StepTwo.propTypes = {
  register: PropTypes.func.isRequired,
  errors: PropTypes.shape(),
  sendStatus: PropTypes.string,
  termsAccepted: PropTypes.bool.isRequired,
  setTermsAccepted: PropTypes.func.isRequired,
  setShowTermsPopup: PropTypes.func.isRequired,
};

// --------------------------------- //

const Progress = ({ step }) => {
  return (
    <div className="absolute bg-orange-200 left-0 h-3 bottom-0 w-full">
      <div
        className={classNames(
          "h-full bg-primary transition-width duration-500 ease-in-out",
          {
            "w-1/3": step === STEP_1,
            "w-2/3": step === STEP_2,
            "w-full": step === STEP_3,
          }
        )}
      />
    </div>
  );
};

Progress.propTypes = {
  step: PropTypes.string.isRequired,
};

export default BecomeFranchiseeForm;
